import { useState, type ReactNode } from 'react';
import { AlertTriangle, PackagePlus, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { cn } from '@/shared/lib/cn';
import {
  useReposicion,
  type ReposicionDashboard,
  type ReposicionItem,
} from './ventasApi';
import { fmtInt, fmtMonedaCompact } from './dashFormat';
import { EmptyCard, KpiCard, KpiSkeletonRow, SegBar } from './dashUi';

const fmt1 = (n: number) => n.toLocaleString('es-AR', { maximumFractionDigits: 1 });

export function ReposicionView({ leftControls }: { leftControls?: ReactNode }) {
  const [meses, setMeses] = useState(6);
  const [objetivo, setObjetivo] = useState(2);
  const q = useReposicion(meses, objetivo);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">{leftControls}</div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground">Demanda</span>
            <SegBar
              value={String(meses)}
              onChange={(v) => setMeses(Number(v))}
              options={[
                { id: '3', label: '3m' },
                { id: '6', label: '6m' },
                { id: '12', label: '12m' },
              ]}
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground">Objetivo</span>
            <SegBar
              value={String(objetivo)}
              onChange={(v) => setObjetivo(Number(v))}
              options={[
                { id: '1', label: '1m' },
                { id: '2', label: '2m' },
                { id: '3', label: '3m' },
              ]}
            />
          </div>
        </div>
      </div>
      <p className="-mt-1 text-xs text-muted-foreground">
        Compara la <strong>venta mensual promedio</strong> de cada artículo con el stock para sugerir
        qué comprar. Objetivo: cubrir {objetivo} {objetivo === 1 ? 'mes' : 'meses'} de venta.
      </p>

      {q.isError ? (
        <EmptyCard>No se pudo calcular la reposición. Reintentá en unos segundos.</EmptyCard>
      ) : q.isLoading || !q.data ? (
        <KpiSkeletonRow n={3} />
      ) : (
        <ReposicionLoaded data={q.data} />
      )}
    </div>
  );
}

function ReposicionLoaded({ data }: { data: ReposicionDashboard }) {
  const m = data.moneda;
  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard
          label="Comprar urgente"
          value={fmtInt(data.resumen.urgentes)}
          foot={`${fmtInt(data.resumen.unidadesUrgente)} u sugeridas · ${fmtMonedaCompact(data.resumen.valorUrgente, m)}`}
          accent={data.resumen.urgentes > 0 ? '#d03b3b' : undefined}
        />
        <KpiCard
          label="Cubierto por ingreso"
          value={fmtInt(data.resumen.cubiertos)}
          foot="Sin stock, pero con mercadería por ingresar"
        />
        <KpiCard
          label="Reponer pronto"
          value={fmtInt(data.resumen.reponer)}
          foot={`Cobertura < objetivo · ${fmtMonedaCompact(data.resumen.valorReponer, m)}`}
          accent={data.resumen.reponer > 0 ? '#eda100' : undefined}
        />
      </div>

      <ReposCard
        title="Comprar urgente"
        subtitle="Sin stock disponible y sin mercadería por ingresar — se está vendiendo y no hay reposición en camino."
        Icon={AlertTriangle}
        color="#d03b3b"
        items={data.urgentes}
        moneda={m}
        emptyText="Nada urgente. 🎉"
      />

      <ReposCard
        title="Cubierto por lo que ingresa"
        subtitle="Sin stock disponible hoy, pero lo que está por ingresar cubre (total o parcialmente) la demanda."
        Icon={PackagePlus}
        color="#2a78d6"
        items={data.cubiertos}
        moneda={m}
        emptyText="Nada en esta situación."
      />

      <ReposCard
        title="Reponer pronto"
        subtitle="Con stock, pero la cobertura está por debajo del objetivo — conviene reponer antes de quedar en cero."
        Icon={TrendingDown}
        color="#eda100"
        items={data.reponer}
        moneda={m}
        emptyText="Todo con cobertura suficiente."
      />
    </>
  );
}

function ReposCard({
  title,
  subtitle,
  Icon,
  color,
  items,
  moneda,
  emptyText,
}: {
  title: string;
  subtitle: string;
  Icon: typeof AlertTriangle;
  color: string;
  items: ReposicionItem[];
  moneda: string;
  emptyText: string;
}) {
  return (
    <Card>
      <CardHeader className="gap-1 pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Icon className="h-4 w-4" style={{ color }} aria-hidden />
            {title}
          </CardTitle>
          <span
            className="rounded-full px-2 py-0.5 text-xs font-semibold text-white"
            style={{ backgroundColor: color }}
          >
            {fmtInt(items.length)}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="flex h-20 items-center justify-center text-sm text-muted-foreground">
            {emptyText}
          </div>
        ) : (
          <ReposTable items={items} moneda={moneda} />
        )}
      </CardContent>
    </Card>
  );
}

function ReposTable({ items, moneda }: { items: ReposicionItem[]; moneda: string }) {
  const cols: Array<{
    label: string;
    width: string;
    get: (r: ReposicionItem) => string;
    num?: boolean;
    emphasis?: boolean;
  }> = [
    { label: 'Artículo', width: '25%', get: (r) => r.articulo.toUpperCase() },
    { label: 'Tipo', width: '12%', get: (r) => r.tipo },
    { label: 'Venta/mes', width: '10%', get: (r) => `${fmt1(r.ventaMensual)} u`, num: true },
    { label: 'Disp.', width: '8%', get: (r) => fmtInt(r.disponible), num: true },
    { label: 'Por ingr.', width: '9%', get: (r) => fmtInt(r.porIngresar), num: true },
    { label: 'Cob.', width: '8%', get: (r) => `${fmt1(r.cobertura)} m`, num: true },
    { label: 'Cob.+ingr.', width: '11%', get: (r) => `${fmt1(r.coberturaConIngreso)} m`, num: true },
    { label: 'Sugerido', width: '8%', get: (r) => `${fmtInt(r.sugerido)} u`, num: true, emphasis: true },
    {
      label: 'Valor',
      width: '9%',
      get: (r) => (r.valorSugerido != null ? fmtMonedaCompact(r.valorSugerido, moneda) : '—'),
      num: true,
    },
  ];

  return (
    <div className="overflow-x-hidden">
      <table className="w-full table-fixed border-collapse text-sm">
        <colgroup>
          {cols.map((c) => (
            <col key={c.label} style={{ width: c.width }} />
          ))}
        </colgroup>
        <thead>
          <tr className="border-b bg-card">
            {cols.map((c) => (
              <th
                key={c.label}
                className={cn(
                  'px-2 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground',
                  c.num ? 'text-right' : 'text-left',
                )}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((r, i) => (
            <tr key={`${r.articulo}-${i}`} className={cn(i % 2 === 1 && 'bg-muted/30', 'hover:bg-muted/60')}>
              {cols.map((c) => (
                <td
                  key={c.label}
                  title={c.label === 'Artículo' ? r.articulo.toUpperCase() : undefined}
                  className={cn(
                    'overflow-hidden text-ellipsis whitespace-nowrap px-2 py-1.5',
                    c.num ? 'text-right tabular-nums' : 'text-left',
                    c.label === 'Artículo' || c.emphasis
                      ? 'font-medium text-foreground'
                      : 'text-muted-foreground',
                  )}
                >
                  {c.get(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
